import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { CreditCard, Eye, EyeOff, Save, AlertCircle, RefreshCw } from "lucide-react";

const PaymentGatewaySettings = ({ API, token }) => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingGateway, setEditingGateway] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => {
    fetchGateways();
  }, []);

  const fetchGateways = async () => {
    try {
      console.log("Fetching gateways from:", `${API}/admin/payment-gateways`);
      console.log("Token:", token ? "Present" : "Missing");
      
      const response = await axios.get(`${API}/admin/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Gateways response:", response.data);
      
      // Ensure response.data is an array
      const gatewaysData = Array.isArray(response.data) ? response.data : [];
      setGateways(gatewaysData);
      
      if (gatewaysData.length === 0) {
        toast.warning("No payment gateways configured yet");
      } else {
        toast.success(`Loaded ${gatewaysData.length} payment gateways`);
      }
    } catch (error) {
      console.error("Error fetching gateways:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      const errorMsg = error.response?.data?.detail || error.message || "Failed to fetch payment gateways";
      toast.error(errorMsg);
      setGateways([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const updateGateway = async (gatewayId, data) => {
    try {
      const url = `${API}/admin/payment-gateways/${gatewayId}`;
      console.log("=== UPDATE GATEWAY DEBUG ===");
      console.log("Gateway ID:", gatewayId);
      console.log("Update data:", data);
      console.log("Full URL:", url);
      console.log("API:", API);
      console.log("Token exists:", !!token);
      console.log("Token value:", token ? token.substring(0, 20) + "..." : "NO TOKEN");
      
      const response = await axios.put(url, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Update response:", response.data);
      toast.success("Payment gateway updated successfully");
      fetchGateways();
      setEditingGateway(null);
    } catch (error) {
      console.error("Error updating gateway:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      const errorMsg = error.response?.data?.detail || error.message || "Failed to update payment gateway";
      toast.error(errorMsg);
      throw error; // Re-throw so handleSave knows it failed
    }
  };

  const toggleGateway = async (gatewayId, currentStatus) => {
    try {
      console.log("Toggling gateway:", gatewayId, "from", currentStatus, "to", !currentStatus);
      await updateGateway(gatewayId, { is_enabled: !currentStatus });
    } catch (error) {
      console.error("Toggle gateway failed:", error);
      // Error already shown by updateGateway
    }
  };

  const GatewayCard = ({ gateway }) => {
    const [formData, setFormData] = useState({
      environment: gateway.environment || "test",
      api_key: "",
      api_secret: "",
      merchant_id: "",
      webhook_secret: ""
    });
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleSave = async () => {
      try {
        const updateData = {};
        if (formData.api_key) updateData.api_key = formData.api_key;
        if (formData.api_secret) updateData.api_secret = formData.api_secret;
        if (formData.merchant_id) updateData.merchant_id = formData.merchant_id;
        if (formData.webhook_secret) updateData.webhook_secret = formData.webhook_secret;
        updateData.environment = formData.environment;
        
        await updateGateway(gateway.id, updateData);
        setIsDialogOpen(false);
        
        // Reset form
        setFormData({
          environment: gateway.environment || "test",
          api_key: "",
          api_secret: "",
          merchant_id: "",
          webhook_secret: ""
        });
      } catch (error) {
        // Error already handled in updateGateway, just keep dialog open
        console.error("Save failed:", error);
      }
    };

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-blue-600" />
              <div>
                <CardTitle>{gateway.display_name}</CardTitle>
                <CardDescription className="capitalize">{gateway.gateway_name}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={gateway.is_enabled ? "success" : "secondary"}>
                {gateway.is_enabled ? "Enabled" : "Disabled"}
              </Badge>
              <Badge variant={gateway.environment === "live" ? "destructive" : "outline"}>
                {gateway.environment}
              </Badge>
              <Switch
                checked={gateway.is_enabled}
                onCheckedChange={() => toggleGateway(gateway.id, gateway.is_enabled)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {gateway.is_enabled && !gateway.api_key && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold">Configuration Required</p>
                  <p>Please configure API keys to use this gateway</p>
                </div>
              </div>
            )}

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full">
                  {gateway.api_key ? "Update Configuration" : "Configure Gateway"}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Configure {gateway.display_name}</DialogTitle>
                  <DialogDescription>
                    Enter your {gateway.display_name} credentials. All fields are securely stored.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="environment">Environment</Label>
                    <Select
                      value={formData.environment}
                      onValueChange={(value) => setFormData({...formData, environment: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Test/Sandbox</SelectItem>
                        <SelectItem value="live">Live/Production</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="api_key">API Key / Client ID</Label>
                    <Input
                      id="api_key"
                      type="text"
                      placeholder={gateway.api_key ? "Enter new key to update" : "Enter API Key"}
                      value={formData.api_key}
                      onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="api_secret">API Secret / Client Secret</Label>
                    <div className="flex gap-2">
                      <Input
                        id="api_secret"
                        type={showSecrets[gateway.id] ? "text" : "password"}
                        placeholder={gateway.api_secret ? "Enter new secret to update" : "Enter API Secret"}
                        value={formData.api_secret}
                        onChange={(e) => setFormData({...formData, api_secret: e.target.value})}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowSecrets({...showSecrets, [gateway.id]: !showSecrets[gateway.id]})}
                      >
                        {showSecrets[gateway.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {['phonepe', 'paytm'].includes(gateway.gateway_name) && (
                    <div>
                      <Label htmlFor="merchant_id">Merchant ID</Label>
                      <Input
                        id="merchant_id"
                        type="text"
                        placeholder="Enter Merchant ID"
                        value={formData.merchant_id}
                        onChange={(e) => setFormData({...formData, merchant_id: e.target.value})}
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="webhook_secret">Webhook Secret (Optional)</Label>
                    <Input
                      id="webhook_secret"
                      type={showSecrets[gateway.id + '_webhook'] ? "text" : "password"}
                      placeholder="Enter Webhook Secret"
                      value={formData.webhook_secret}
                      onChange={(e) => setFormData({...formData, webhook_secret: e.target.value})}
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Currently in mock mode for testing. You can add real API keys 
                      to enable live payments. Leave fields empty to keep using mock mode.
                    </p>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Configuration
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {gateway.api_key && (
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">API Key:</span>
                  <span className="font-mono">{gateway.api_key}</span>
                </div>
                {gateway.api_secret && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">API Secret:</span>
                    <span className="font-mono">{gateway.api_secret}</span>
                  </div>
                )}
                {gateway.merchant_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Merchant ID:</span>
                    <span className="font-mono">{gateway.merchant_id}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading payment gateways...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold">Payment Gateway Configuration</h3>
          <p className="text-gray-600">Configure and manage payment gateways for your platform</p>
        </div>
        <Button onClick={fetchGateways} variant="outline">
          Refresh
        </Button>
      </div>

      {gateways.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h4 className="text-lg font-semibold mb-2">No Payment Gateways Found</h4>
            <p className="text-gray-600 mb-4">
              Payment gateways should be automatically initialized. Click refresh to try again.
            </p>
            <Button onClick={fetchGateways}>
              Refresh Gateways
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {gateways.map((gateway) => (
            <GatewayCard key={gateway.id} gateway={gateway} />
          ))}
        </div>
      )}

      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Payment Gateway Information</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Enable/disable gateways using the toggle switch</li>
                <li>• Configure API keys for each gateway in test or live mode</li>
                <li>• Mock mode is active when no API keys are configured</li>
                <li>• Users will only see enabled gateways during checkout</li>
                <li>• Switch to live mode only after thorough testing</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentGatewaySettings;
